import {analyticsLogger} from 'analytics/analyticsLogger';

import {workstreamsPageOf} from './workstreamsRoutes';

/**
 * What opened a /workstreams view, carried in the navigation's router state
 * (see `viaState`) so the view event can say. Back / Forward and page loads
 * report 'history_or_load'; an in-app link that doesn't set one, 'link'.
 */
export type WorkstreamsVia =
  | 'rail_my'
  | 'rail_company'
  | 'rail_pillar'
  | 'rail_workstream'
  | 'rail_inbox'
  | 'rail_audit'
  | 'recent_workstream'
  | 'recent_person'
  | 'recents_strip'
  | 'search_workstream'
  | 'search_person'
  | 'byline_person'
  | 'section_header'
  | 'card_link'
  | 'detail_page_link'
  | 'back'
  | 'mode_switch'
  | 'wireframes_tab'
  | 'all_quarters'
  | 'create'
  | 'my_empty_company'
  | 'home_tile'
  | 'profile_card';

export type WorkstreamsViewPlace =
  | 'my'
  | 'company'
  | 'pillar'
  | 'person'
  | 'workstream'
  | 'inbox'
  | 'audit';

/**
 * For a view opened from a card: the card's section and the page it was on
 * (the view's own place is then 'workstream'), so the event can say.
 */
export type WorkstreamsViaOrigin = {section?: string; fromPlace?: WorkstreamsViewPlace};

const VIA_STATE_KEY = 'workstreamsVia';
const VIA_ORIGIN_STATE_KEY = 'workstreamsViaOrigin';

export const viaState = (via: WorkstreamsVia, origin?: WorkstreamsViaOrigin) => ({
  [VIA_STATE_KEY]: via,
  ...(origin ? {[VIA_ORIGIN_STATE_KEY]: origin} : {}),
});

export const readVia = (state: unknown): WorkstreamsVia | undefined =>
  state && typeof state === 'object' && VIA_STATE_KEY in state
    ? ((state as Record<string, unknown>)[VIA_STATE_KEY] as WorkstreamsVia)
    : undefined;

export const readViaOrigin = (state: unknown): WorkstreamsViaOrigin | undefined =>
  state && typeof state === 'object' && VIA_ORIGIN_STATE_KEY in state
    ? ((state as Record<string, unknown>)[VIA_ORIGIN_STATE_KEY] as WorkstreamsViaOrigin)
    : undefined;

// Every event here reuses one the old /workstreams page logs, as v2 did: `v4`
// tells them apart, and the series carry on once this page replaces it.
const V4_CONTEXT = {source: 'workstreams_page', v4: true};

export const logWorkstreamsViewed = (view: {
  place: WorkstreamsViewPlace;
  page: 'list' | 'focus' | 'detail';
  mode?: string;
  workstreamId?: number;
  via: WorkstreamsVia | 'history_or_load' | 'link';
  origin?: WorkstreamsViaOrigin;
  timeframe: string;
  // The rail's recents as the view opens: how many show, and how many are pinned.
  recents: {shown: number; pinned: number};
}) => {
  const props = {
    ...V4_CONTEXT,
    // `type` is what the old page's WORKSTREAM_VIEW calls the scope.
    type: view.place,
    page: view.page,
    mode: view.mode,
    workstream_id: view.workstreamId,
    via: view.via,
    section: view.origin?.section,
    from_place: view.origin?.fromPlace,
    timeframe: view.timeframe,
    recent_count: view.recents.shown,
    pinned_count: view.recents.pinned,
  };
  analyticsLogger().logEvent(
    view.page === 'detail' ? 'WORKSTREAM_DETAIL_VIEW' : 'WORKSTREAM_VIEW',
    props
  );
};

/**
 * An in-place action on /workstreams, as WORKSTREAM_VIEW_CLICKED's `type`
 * (navigations are the view events' `via`).
 */
export type WorkstreamsAction =
  | 'card_toggled'
  | 'fold_toggled'
  | 'section_toggled'
  | 'staffing_opened'
  | 'rail_group_toggled'
  | 'recent_pinned'
  | 'search_opened'
  | 'mobile_nav_opened'
  | 'narrative_link_opened'
  | 'roadmap_link_opened'
  | 'roadmap_embed_toggled'
  | 'roadmap_added'
  | 'roadmap_edited'
  | 'roadmap_removed'
  | 'person_profile_opened'
  | 'timeframe_changed'
  | 'audit_scope_changed'
  | 'update_opened'
  | 'update_history_selected'
  | 'update_menu_opened'
  | 'update_edit_opened'
  | 'update_edit_saved'
  | 'update_delete_clicked'
  | 'update_deleted'
  | 'full_update_toggled'
  | 'summary_edit_opened'
  | 'summary_edit_saved'
  | 'edit_opened'
  | 'subworkstream_linked'
  | 'subworkstream_created'
  | 'subworkstream_removed'
  | 'subworkstream_remove_undone'
  | 'subworkstreams_reordered'
  | 'workstream_created'
  | 'detail_opened_new_tab'
  | 'my_empty_shown';

/**
 * Which button opened the update modal, for update_opened: a card's status
 * (with the card's section and page) or the detail page's next-update prompt.
 */
export type UpdateOpenedFrom =
  | {button: 'card_status'; section?: string; from_place?: WorkstreamsViewPlace}
  | {button: 'next_update_prompt'};

/**
 * A click the browser opens in a new tab or window (a middle-click, or a left
 * one with cmd / ctrl / shift), which the page never sees as a navigation.
 * Alt-click downloads the link instead, so it doesn't count.
 */
export const opensInNewTab = (event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
}) =>
  event.button === 1 || (event.button === 0 && (event.metaKey || event.ctrlKey || event.shiftKey));

export const logWorkstreamsInteraction = (
  action: WorkstreamsAction,
  props: {[key: string]: boolean | number | string | undefined} = {}
) => {
  analyticsLogger().logEvent('WORKSTREAM_VIEW_CLICKED', {
    ...V4_CONTEXT,
    type: action,
    page: workstreamsPageOf(window.location.pathname),
    ...props,
  });
};
